import React from 'react';
import {makeStyles} from "@mui/styles";
import Paper from '@mui/material/Paper';
import {Divider, MenuItem, Select as MuiSelect} from "@mui/material";
import {KeyboardArrowDown} from '@mui/icons-material';
import Select from "../../../custom/mui_rff/select";
import classNames from 'classnames';
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";

/**
 * The Menu props for blurring the dropdown after its been opened and to bring the menu to the front to prevent
 * its edges being clipped.
 */
const defaultMenuProps = () => ({
    TransitionProps: {
        onEnter: () => {
            setTimeout(() => {
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            }, 500);
        },
    },
    PaperProps: {
        elevation: 4,
        style: {
            transform: 'translate3d(0, 0, 0)'
        }
    },
    MenuListProps: {
        classes: {},
    },
    anchorOrigin: {
        vertical: "bottom",
        horizontal: "center"
    },
})

const useStyles = makeStyles((theme) => ({
    root: {
        padding: props => props.padding ?? '5px 15px',
        display: 'flex',
        alignItems: 'center',
        color: theme.vars.palette.background.paper,
        boxShadow: 'none',
        // boxShadow: props => props.shadow ? `0px 7px 7px ${theme.vars.palette['app-specific'].shadow}` : 'none',
    },
    icon: {
        transition: 'all 0.2s ease-in-out',
        cursor: props => (!props.data || !props.data.length) ? 'not-allowed' : 'pointer',
        marginRight: 4,
        marginLeft: 4,
        stroke: 'none',
        width: '1em',
        height: '1em',
    },
    label: {
        backgroundColor: theme.vars.palette.background.paper,
        transform: 'translate(0, 10px) scale(1)',
    },
    shrink: {
        backgroundColor: theme.vars.palette.background.paper,
        padding: '10px 10px',
        transform: 'translate(0, -4px) scale(0.75)',
    }
}));

export const Dropdown = ({
                             placeholder = 'type...',
                             label,
                             ariaLabel = 'input',
                             value = '',
                             onChange,
                             name = label,
                             type,
                             fullWidth = true,
                             data,
                             shadow = true,
                             className = '',
                             renderSelectedValue,
                             multiple,
                         }) => {
    const classes = useStyles({shadow, data});

    return (
        <Paper className={classNames(classes.root, 'input-with-paper')}>
            <FormControl variant={'standard'} className={`${className}`} aria-label={ariaLabel} fullWidth={fullWidth}>
                <InputLabel htmlFor={label}>{label}</InputLabel>
                <MuiSelect
                    disabled={!data || !data.length}
                    fullWidth={fullWidth}
                    autoWidth={false}
                    type={type}
                    variant={'standard'}
                    label={label}
                    name={name}
                    value={value}
                    IconComponent={(props) => (
                        <>
                            <Divider className={classNames('select-divider divider-card', props.className)}
                                     orientation={'vertical'}/>
                            <KeyboardArrowDown className={classNames([props.className, classes.icon])}/>
                        </>
                    )}
                    onChange={onChange}
                    className={classes.input}
                    placeholder={placeholder}
                    renderValue={(value => {
                        if (renderSelectedValue) {
                            return renderSelectedValue(value);
                        }
                        if (multiple) return <p
                            className='text-wrap'>{value.map(e => <span key={e.label ?? e.name ?? e.title}
                                                                        className='mr-3'>{e.label ?? e.name ?? e.title}</span>)}</p>
                        return <p>{(value.label ?? value.name ?? value.title ?? value.description ?? value) ?? ''}</p>
                    })}
                    disableUnderline
                    inputProps={{'aria-label': ariaLabel, className: classes.innerInput}}
                    MenuProps={defaultMenuProps()}
                >
                    {data && data?.map(item => (
                        <MenuItem
                            value={item.value}
                            key={item.id}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </MuiSelect>
            </FormControl>
        </Paper>
    );
}


export const DropdownForm = ({
                                 placeholder = 'select...',
                                 label,
                                 ariaLabel = 'input',
                                 required = false,
                                 fullWidth = true,
                                 helperText,
                                 fieldProps,
                                 inputLabelProps,
                                 formControlProps,
                                 formHelperTextProps,
                                 name,
                                 padding,
                                 data,
                                 onChange,
                                 multiple = false,
                                 shadow = true,
                                 renderSelectedValue,
                                 renderMultipleSelectValue,
                                 ...props
                             }) => {
    const classes = useStyles({shadow, data, padding});

    return (
        <Paper className={classNames(classes.root, 'input-with-paper')}>
            <Select
                onChange={onChange}
                disabled={!data || !data.length}
                fullWidth={fullWidth}
                autoWidth={false}
                required={required}
                label={label}
                name={name}
                multiple={multiple}
                IconComponent={(props) => (
                    <>
                        <Divider className={classNames('select-divider divider-card', props.className)}
                                 orientation={'vertical'}/>
                        <KeyboardArrowDown className={classNames([props.className, classes.icon])}/>
                    </>
                )}
                placeholder={placeholder}
                disableUnderline
                inputProps={{'aria-label': ariaLabel}}
                variant={'standard'}
                data={data === '' ? '' : null}
                renderValue={(value => {
                    if (renderSelectedValue) {
                        return renderSelectedValue(value);
                    }
                    if (multiple)
                        return (
                            <div className='d-flex flex-wrap w-100'>
                                {
                                    value.map((e, index, array) => {
                                        return !!renderMultipleSelectValue
                                            ? renderMultipleSelectValue(e, index, array?.length)
                                            : <p key={e.id ?? e.label ?? e.name ?? e.title} className='mr-2 mt-2'>
                                                {e.label ?? e.name ?? e.title ?? ''},
                                            </p>
                                    })
                                }
                            </div>
                        )
                    return (
                        <p>
                            {(value.label ?? value.name ?? value.title ?? value.description ?? value) ?? ''}
                        </p>
                    )
                })}
                helperText={helperText}
                fieldProps={fieldProps}
                inputLabelProps={inputLabelProps}
                formControlProps={formControlProps}
                formHelperTextProps={formHelperTextProps}
                MenuProps={defaultMenuProps()}
                {...props}
            >
                {data && data !== '' && data.map(item => (
                    <MenuItem
                        value={item.value}
                        key={item.id}
                        disabled={item.disabled}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </Select>
        </Paper>
    );
}
